import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import {
  useActiveDivisionId,
  usePoolsQueryOptions,
  useTeamsQueryOptions,
  useWaitlist,
} from "@/components/tournaments/context";
import { useTRPC } from "@/integrations/trpc/react";
import { playerNames } from "@/utils/profiles";

export type RemoveTeamFormProps = {
  tournamentDivisionTeamId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function RemoveTeamForm({
  tournamentDivisionTeamId,
  ...props
}: RemoveTeamFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const tournamentDivisionId = useActiveDivisionId();
  const teamQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();
  const withdrawnTeamsQueryOptions =
    trpc.tournaments.getWithdrawnTeams.queryOptions({ tournamentDivisionId });

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.removeTeam.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamQueryOptions);
      queryClient.invalidateQueries(poolsQueryOptions);
      queryClient.invalidateQueries(withdrawnTeamsQueryOptions);
      queryClient.invalidateQueries(
        trpc.tournaments.getTournamentTeamStats.pathFilter(),
      );

      props.onOpenChange(false);
    },
  });

  const waitlist = useWaitlist();

  const form = useAppForm({
    defaultValues: {
      refundStrategy: "default" as "default" | "full" | "none",
      replace: "no",
      replacementTeamId: null as number | null,
    },
    listeners: {
      onChange: ({ formApi, fieldApi }) => {
        if (fieldApi.name === "replace") {
          if (fieldApi.state.value === "yes") {
            if (waitlist && waitlist.length > 0) {
              formApi.setFieldValue("replacementTeamId", waitlist[0].id);
            }
          } else {
            formApi.setFieldValue("replacementTeamId", null);
          }
        }
      },
    },
    onSubmit: ({ value: { refundStrategy, replacementTeamId } }) => {
      mutate({
        id: tournamentDivisionTeamId,
        refundStrategy,
        replacementTeamId,
      });
    },
  });

  useEffect(() => {
    if (!props.isOpen) {
      form.reset();
    }
  }, [props.isOpen, form]);

  return (
    <Modal {...props}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Remove Team?
        </Heading>

        <p className="mb-8 text-sm text-gray-700">
          Are you sure you want to remove this team from the division?
        </p>

        <form
          className="flex flex-col space-y-2"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to remove team"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField name="refundStrategy">
            {(field) => (
              <field.RadioGroup
                field={field}
                mode="string"
                label="Refund Strategy"
                orientation="vertical"
              >
                <Radio value="default">
                  <div>
                    <div>Refund less processing fee (default policy)</div>
                    <div className="text-xs text-gray-500">
                      Applies withdrawal deadline window. Refund is tournament
                      fee minus processing fee if inside window, otherwise $0.
                    </div>
                  </div>
                </Radio>
                <Radio value="full">
                  <div>
                    <div>Full refund</div>
                    <div className="text-xs text-gray-500">
                      Refund full registration fee (exceptional circumstances)
                    </div>
                  </div>
                </Radio>
                <Radio value="none">
                  <div>
                    <div>No refund</div>
                    <div className="text-xs text-gray-500">
                      Late withdrawal — no refund issued
                    </div>
                  </div>
                </Radio>
              </field.RadioGroup>
            )}
          </form.AppField>

          <form.AppField name="replace">
            {(field) => (
              <field.RadioGroup
                field={field}
                mode="string"
                label="Replace from waitlist?"
                orientation="horizontal"
                isDisabled={waitlist ? waitlist.length === 0 : true}
                description="Replaces the team in their pool and matches with a team from the waitlist and uses the same seed."
              >
                <Radio value="yes">Yes</Radio>
                <Radio value="no">No</Radio>
              </field.RadioGroup>
            )}
          </form.AppField>

          <form.AppField name="replacementTeamId">
            {(field) => (
              <form.Subscribe
                selector={({ values: { replace, replacementTeamId } }) => [
                  replace,
                  replacementTeamId,
                ]}
              >
                {([replace, replacementTeamId]) =>
                  replace === "yes" && (
                    <field.Select
                      key={replacementTeamId}
                      label="Replacement Team"
                      options={
                        waitlist?.map((team) => ({
                          value: team.id,
                          display: playerNames(
                            team.team.players.map(({ profile }) => profile),
                          ).join(" & "),
                        })) ?? []
                      }
                      field={field}
                    />
                  )
                }
              </form.Subscribe>
            )}
          </form.AppField>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => props.onOpenChange(false)}>Cancel</Button>

              <form.Subscribe
                selector={(state) => state.values.refundStrategy}
                children={(refundStrategy) =>
                  refundStrategy === "full" ? (
                    <form.ConfirmSubmitButton
                      requireChange={false}
                      description={
                        <p className="text-sm text-gray-700">
                          Full refunds should only be issued in exceptional
                          circumstances. This will refund the full registration
                          fee to the team's payment method. Are you sure?
                        </p>
                      }
                    >
                      Confirm
                    </form.ConfirmSubmitButton>
                  ) : (
                    <form.SubmitButton requireChange={false}>
                      Confirm
                    </form.SubmitButton>
                  )
                }
              />
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
